import { missingSchedulingDate } from '../../../utils/prepullRecovery';
import { SchedulingLeg } from '../../../types/scheduling';
import { useAltHotkeys } from '../../../hooks/useAltHotkeys';
import { useModalDrag } from '../../../hooks/useModalDrag';
import ModalHeader from '../../../components/ui/ModalHeader';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';

interface SendToLCModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  selectedCount: number;
  legs: SchedulingLeg[];
  isLoading?: boolean;
}

export default function SendToLCModal({
  isOpen,
  onClose,
  onConfirm,
  selectedCount,
  legs,
  isLoading = false,
}: SendToLCModalProps) {
  // Check if any legs are missing both proposed dates (need at least one)
  const legsWithoutDates = legs.filter(missingSchedulingDate);

  // Helper to check if a leg is a rail order
  const isRailOrder = (leg: SchedulingLeg) =>
    ((leg.mode === 'MLB' && leg.move_type === 'IMPORT') ||
    leg.move_type === 'RAIL TO PORT') &&
    (leg.orig_location_type === 'RAIL' || leg.orig_require_pickup === 'Y');

  // Check if any rail legs are missing rail pickup number (only required for rail orders)
  const legsWithoutRailPickup = legs.filter((leg) => isRailOrder(leg) && !leg.rail_pickup_number?.trim());

  // Cannot send if missing dates or rail pickup number
  const canSend = legsWithoutRailPickup.length === 0 && legsWithoutDates.length === 0 && !isLoading;

  // Alt+C closes. Send gets no chord — it hands the legs off to LC.
  useAltHotkeys({ C: isLoading ? null : onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        aria-labelledby="send-to-lc-modal-title"
        onClose={isLoading ? () => {} : onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-scrim bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Panel
                ref={panelRef}
                style={panelStyle}
                className="w-full max-w-md overflow-hidden rounded-xl bg-panel shadow-xl"
              >
                <ModalHeader
                  title="Send to LC"
                  titleId="send-to-lc-modal-title"
                  onClose={isLoading ? undefined : onClose}
                  dragHandleProps={dragHandleProps}
                />
                <div className="p-6">
                {isLoading ? (
                  // Loading state
                  <div className="flex flex-col items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                    <p className="text-lg font-medium text-heading">
                      Sending to LC...
                    </p>
                    <p className="text-sm text-muted mt-2">
                      Please wait while we process {selectedCount} leg{selectedCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                ) : (
                  // Normal state
                  <>
                    <div className="mb-6">
                      <p className="text-gray-600 dark:text-gray-300">
                        Are you sure you want to send {selectedCount} leg
                        {selectedCount !== 1 ? 's' : ''} to LC for processing?
                      </p>

                      {legsWithoutRailPickup.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                          <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                            Cannot Send: {legsWithoutRailPickup.length} leg
                            {legsWithoutRailPickup.length !== 1 ? 's' : ''} missing
                            Rail Pickup Number
                          </p>
                          <ul className="mt-2 text-xs text-red-700 dark:text-red-300 space-y-1">
                            {legsWithoutRailPickup.slice(0, 5).map((leg) => (
                              <li key={`${leg.pronumber}-${leg.leg_num}`}>
                                Pro #{leg.pronumber} Leg {leg.leg_num}
                              </li>
                            ))}
                            {legsWithoutRailPickup.length > 5 && (
                              <li>...and {legsWithoutRailPickup.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {legsWithoutDates.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                          <p className="text-sm text-red-800 dark:text-red-200 font-medium">
                            Cannot Send: {legsWithoutDates.length} leg
                            {legsWithoutDates.length !== 1 ? 's' : ''} missing
                            required proposed date (pre-pull for confirmed deliveries)
                          </p>
                          <ul className="mt-2 text-xs text-red-700 dark:text-red-300 space-y-1">
                            {legsWithoutDates.slice(0, 5).map((leg) => (
                              <li key={`${leg.pronumber}-${leg.leg_num}`}>
                                Pro #{leg.pronumber}
                              </li>
                            ))}
                            {legsWithoutDates.length > 5 && (
                              <li>...and {legsWithoutDates.length - 5} more</li>
                            )}
                          </ul>
                        </div>
                      )}

                      {/* Summary of selected legs */}
                      <div className="mt-4 max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead className="text-muted sticky top-0 bg-panel">
                            <tr>
                              <th className="text-left py-1">Pro #</th>
                              <th className="text-left py-1">Leg</th>
                              <th className="text-left py-1">Customer</th>
                              <th className="text-left py-1">Rail Pickup #</th>
                              <th className="text-left py-1">Appt Date</th>
                            </tr>
                          </thead>
                          <tbody className="text-label">
                            {legs.map((leg) => (
                              <tr key={`${leg.pronumber}-${leg.leg_num}`}>
                                <td className="py-1">{leg.pronumber}</td>
                                <td className="py-1">{leg.leg_num}</td>
                                <td className="py-1 truncate max-w-[100px]">
                                  {leg.customer || '-'}
                                </td>
                                <td className={`py-1 ${isRailOrder(leg) && !leg.rail_pickup_number?.trim() ? 'text-red-500 font-medium' : ''}`}>
                                  {isRailOrder(leg) ? (leg.rail_pickup_number || 'Missing') : (leg.rail_pickup_number || '-')}
                                </td>
                                <td className="py-1">
                                  {leg.proposed_appt_date
                                    ? new Date(leg.proposed_appt_date + 'T00:00:00').toLocaleDateString()
                                    : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={onConfirm}
                        disabled={!canSend}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-blue-600"
                      >
                        <PaperAirplaneIcon className="w-4 h-4" />
                        Send to LC
                      </button>
                    </div>
                  </>
                )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
